import { useEffect, useRef } from 'react';
import '../LandingPage.css';

const TAGLINE =
  'The people who build your application are the people who look after it once it is live.';

/**
 * Large type statement whose words light up one at a time, in reading order,
 * as each crosses a trigger line 60% down the viewport. One observer tracks
 * every word; scrolling back up dims them again. Without IntersectionObserver
 * (or with reduced motion, via CSS) the text simply shows at full colour.
 */
function TaglineSection() {
  const textRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const root = textRef.current;
    if (!root) return;
    const words = Array.from(root.querySelectorAll<HTMLElement>('.tagline-word'));

    if (!('IntersectionObserver' in window)) {
      words.forEach((word) => word.classList.add('is-lit'));
      return;
    }

    root.classList.add('tagline-armed');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Lit while inside the band above the trigger line, or once scrolled past it.
          const lit = entry.isIntersecting || entry.boundingClientRect.top < 0;
          entry.target.classList.toggle('is-lit', lit);
        });
      },
      // root: document makes rootMargin apply even when the page is embedded in a frame.
      { root: document, rootMargin: '0px 0px -40% 0px' }
    );
    words.forEach((word) => observer.observe(word));

    return () => {
      observer.disconnect();
      root.classList.remove('tagline-armed');
    };
  }, []);

  return (
    <section id="promise" className="tagline-section section" aria-label="What working with EFSoft means">
      <div className="section-content">
        <p ref={textRef} className="tagline-text">
          {TAGLINE.split(' ').map((word, index) => (
            <span key={index} className="tagline-word">
              {word}{' '}
            </span>
          ))}
        </p>
      </div>
    </section>
  );
}

export default TaglineSection;
